import React, { useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import SubPages from "./SubPagesEnum";
import motionify from "../../components/Motion";
import apiService from "../../services/ApiService";
import snakeToPascal from "../../tools";
import TutorialStep, { TutorialContext, TutorialsReferences } from "../../components/Tutorial";
import { useAppNavigation } from "../../hooks/useAppNavigation";
import { useAppStore } from "../../shared/stores/useAppStore";
import { MODELS_QUERY_KEY } from "../../shared/hooks/queries/useModels";
import { cn } from "../../lib/utils";
import TailwindButton from "../../components/Tailwind/TailwindButton";

const LOGO_BY_PRESET = {
    'blank': 'insert_drive_file',
    'basic': 'account_balance',
    'wind_power': 'wind_power',
    'solar_power': 'solar_power',
}

const DESCRIPTION_BY_PRESET = {
    'blank': 'Start from scratch with an empty model',
    'basic': 'A standard financial model template',
    'wind_power': 'Pre-configured for wind energy projects',
    'solar_power': 'Pre-configured for solar energy projects',
}

export default function NewModel({ entity }) {
    const navigateToPage = useAppNavigation();
    const queryClient = useQueryClient();

    const [presetList, setPresetList] = useState([]);
    const [applying, setApplying] = useState<string | null>(null);
    const { tutorialEnabled } = useContext(TutorialContext);
    const { currentTutorialSteps } = useContext(TutorialContext);

    const loadPresets = () => {
        apiService.getPresetList(entity.id).then(async(response) => {
            if (response) {
                if (response.ok) {
                    const lst = await response.json()
                    setPresetList(lst);
                }
            }
        })
    }

    const applyPreset = (entity, preset) => {
        setApplying(preset);
        apiService.applyPreset(entity.id, preset).then(async(response) => {
            if (response) {
                if (response.ok) {
                    queryClient.invalidateQueries({ queryKey: MODELS_QUERY_KEY });
                    navigateToPage(SubPages.FinancialModelEditor, entity);
                } else {
                    setApplying(null);
                    useAppStore.getState().openErrorSnackBar();
                }
            } else {
                setApplying(null);
            }
        })
    }

    const getIconName = (preset) => {
        return LOGO_BY_PRESET[preset] || 'account_balance';
    }

    const getDescription = (preset) => {
        return DESCRIPTION_BY_PRESET[preset] || 'Pre-built financial model template';
    }

    useEffect(() => {
        loadPresets();
    }, [])

    const PresetCard = ({ preset, disabled = false, onClick }: { preset: string; disabled?: boolean; onClick?: () => void }) => (
        <button
            disabled={disabled || applying !== null}
            className={cn(
                "relative flex flex-col items-center w-full rounded-xl border transition-all duration-200 p-6 text-left group",
                disabled
                    ? "opacity-40 cursor-not-allowed border-slate-200 bg-slate-50"
                    : applying !== null
                        ? "cursor-not-allowed border-slate-200 bg-white opacity-50"
                        : "border-slate-200 bg-white hover:border-primary hover:shadow-lg hover:shadow-primary/10 cursor-pointer",
                applying === preset && "!opacity-100 border-primary ring-2 ring-primary/20 shadow-lg shadow-primary/10"
            )}
            onClick={onClick}
        >
            <div className={cn(
                "size-16 rounded-xl flex items-center justify-center mb-4 transition-colors",
                disabled
                    ? "bg-slate-100 text-slate-400"
                    : applying === preset
                        ? "bg-primary text-white"
                        : "bg-slate-100 text-slate-500 group-hover:bg-primary group-hover:text-white"
            )}>
                <span className="material-symbols-outlined text-[36px]">
                    {getIconName(preset)}
                </span>
            </div>
            <h3 className="text-sm font-bold text-slate-900 text-center mb-1">
                {snakeToPascal(preset)}
            </h3>
            <p className="text-[11px] text-slate-500 text-center leading-tight">
                {getDescription(preset)}
            </p>
            {applying === preset && (
                <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/60">
                    <div className="flex items-center gap-2 text-primary">
                        <svg className="animate-spin size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span className="text-xs font-semibold">Applying...</span>
                    </div>
                </div>
            )}
        </button>
    );

    const isTutorialMode = tutorialEnabled && currentTutorialSteps['main'] < TutorialsReferences['main'].length;

    return (
        motionify(
            <div className="flex-grow bg-white min-h-full">
                <TutorialStep tutorial='main' identifier='preset_page' display='inline-block' position="center" marginTop={20}>
                    <div></div>
                </TutorialStep>

                {/* Page Header */}
                <div className="px-8 py-6 border-b border-slate-100 bg-white sticky top-0 z-10">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
                        <div className="flex-1">
                            <h2 className="text-3xl font-black text-slate-900 tracking-tight">
                                Choose a Preset
                            </h2>
                            <p className="text-sm text-slate-500 mt-2">
                                Select a template for <strong>{entity.name}</strong> or start from scratch
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <TailwindButton
                                variant="ghost"
                                icon="arrow_back"
                                size="sm"
                                disabled={applying !== null}
                                onClick={() => navigateToPage(SubPages.MyModels)}
                            >
                                Back to Models
                            </TailwindButton>
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="px-8 py-6 relative">
                    {/* Loading Backdrop */}
                    {applying !== null && (
                        <div className="absolute inset-0 bg-white/40 z-10 rounded-xl" />
                    )}

                    {isTutorialMode ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            <PresetCard preset="blank" disabled />
                            <TutorialStep tutorial='main' identifier='preset_selection' display='inline-block' position="none" marginTop={-20} marginLeft={30}>
                                <PresetCard
                                    preset="Trial - Wind farm project"
                                    onClick={() => applyPreset(entity, 'Trial - Wind farm project')}
                                />
                            </TutorialStep>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {presetList.map((preset, index) => (
                                <PresetCard
                                    key={index}
                                    preset={preset}
                                    onClick={() => applyPreset(entity, preset)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
        )
    );
}
